/**
 * Web Push subscription helper.
 *
 * Public API:
 *  - isPushSupported()         → true if SW + PushManager are available
 *  - getNotificationPermission()
 *  - getCurrentSubscription()  → existing PushSubscription | null
 *  - subscribePush()           → asks user permission + subscribes + saves to backend
 *  - unsubscribePush()         → removes subscription locally and on backend
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getNotificationPermission = () => {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
};

export const getCurrentSubscription = async () => {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

const detectPlatform = () => {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const getDeviceId = () => {
  let id = localStorage.getItem('allmx_pwa_device_id');
  if (!id) {
    id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    localStorage.setItem('allmx_pwa_device_id', id);
  }
  return id;
};

export const subscribePush = async () => {
  if (!isPushSupported()) throw new Error('Push non supportato su questo dispositivo');
  if (!VAPID_PUBLIC_KEY) throw new Error('VAPID public key non configurata');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permesso negato per le notifiche');
  }

  const reg = await navigator.serviceWorker.ready;
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = subscription.toJSON();
  await fetch(`${BACKEND_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      expirationTime: json.expirationTime || null,
      device_id: getDeviceId(),
      platform: detectPlatform(),
    }),
  });

  return subscription;
};

export const unsubscribePush = async () => {
  if (!isPushSupported()) return false;
  const subscription = await getCurrentSubscription();
  if (!subscription) return false;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  try {
    await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    });
  } catch (_) {
    /* ignore */
  }
  return true;
};
